/**
 * 教师指导系统
 * 管理教师出现的时机和提示内容
 */

import React, { useState, useEffect } from 'react';
import TeacherAvatar from './TeacherAvatar';

interface TeacherGuidanceProps {
  messageCount: number;
  practiceReady: boolean;
  currentScreen: 'chat' | 'practice' | 'profile';
  lastUserMessage?: string;
  onNavigateToPractice?: () => void;
}

interface GuidanceState {
  showTeacher: boolean;
  message: string;
  position: 'bottom-right' | 'bottom-left' | 'center';
}

const TeacherGuidance: React.FC<TeacherGuidanceProps> = ({
  messageCount,
  practiceReady,
  currentScreen,
  lastUserMessage,
  onNavigateToPractice,
}) => {
  const [guidance, setGuidance] = useState<GuidanceState>({
    showTeacher: false,
    message: '',
    position: 'bottom-right',
  });

  const [hasShownWelcome, setHasShownWelcome] = useState(false);
  const [hasShownPracticeReady, setHasShownPracticeReady] = useState(false);
  const [hasShownEncouragement, setHasShownEncouragement] = useState(false);

  // 教师提示语库
  const teacherMessages = {
    welcome: [
      '你好！我是你的英语学习助手小莉老师！😊 让我们一起开始愉快的英语学习之旅吧！',
      '欢迎来到ChatFluent！我会在你的学习过程中给予指导和鼓励～',
      '很高兴见到你！我是你的专属英语导师，有任何问题都可以问我哦！',
    ],
    practiceReady: [
      '太棒了！你已经聊了足够多的内容，现在可以开始翻译练习了！🚀',
      '看起来你准备好挑战翻译练习了！点击右下角的按钮开始吧！',
      '恭喜！你的对话内容已经足够丰富，是时候检验学习成果了！',
    ],
    encouragement: [
      '你做得很好！继续保持这样的学习热情！💪',
      '很棒的对话！你的英语表达能力正在不断提升！',
      '继续加油！每一次练习都让你更接近流利的英语！',
    ],
    practiceCompleted: [
      '完成得很棒！你的翻译能力有了明显进步！🌟',
      '优秀的表现！继续这样练习，你会越来越优秀的！',
      'Well done！你的努力让我很骄傲！',
    ],
    profileVisit: [
      '在这里你可以查看你的学习进度和历史记录！',
      '看看你的学习成果吧！每一次练习都是进步的见证！',
      '你的学习数据显示了很好的进步趋势！继续加油！',
    ],
  };

  const getRandomMessage = (category: keyof typeof teacherMessages): string => {
    const messages = teacherMessages[category];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  const showTeacherMessage = (message: string, position: 'bottom-right' | 'bottom-left' | 'center' = 'bottom-right') => {
    setGuidance({
      showTeacher: true,
      message,
      position,
    });
  };

  const hideTeacher = () => {
    setGuidance(prev => ({
      ...prev,
      showTeacher: false,
    }));
  };

  // 监听不同触发条件
  useEffect(() => {
    if (currentScreen === 'chat') {
      // 强制显示教师测试（调试用）
      if (!hasShownWelcome) {
        setTimeout(() => {
          showTeacherMessage('你好！我是小莉老师！这是一个测试消息，确认我能正常显示！😊', 'bottom-left');
          setHasShownWelcome(true);
        }, 2000);
      }

      // 欢迎消息（首次进入，改为messageCount >= 1）
      // if (messageCount >= 1 && !hasShownWelcome) {
      //   setTimeout(() => {
      //     showTeacherMessage(getRandomMessage('welcome'), 'bottom-left');
      //     setHasShownWelcome(true);
      //   }, 3000); // 增加延迟，确保界面稳定
      // }

      // 练习准备就绪提示（降低门槛到3条消息）
      if (practiceReady && !hasShownPracticeReady && messageCount >= 3) {
        setTimeout(() => {
          showTeacherMessage(getRandomMessage('practiceReady'), 'bottom-left');
          setHasShownPracticeReady(true);
        }, 1000);
      }

      // 鼓励性提示（每5条消息）
      if (messageCount > 0 && messageCount % 5 === 0 && !hasShownEncouragement) {
        setTimeout(() => {
          showTeacherMessage(getRandomMessage('encouragement'), 'bottom-right');
          setHasShownEncouragement(true);
        }, 1500);

        // 重置鼓励标志，允许下次再显示
        setTimeout(() => setHasShownEncouragement(false), 20000);
      }
    }

    if (currentScreen === 'profile') {
      // 个人中心访问提示
      setTimeout(() => {
        showTeacherMessage(getRandomMessage('profileVisit'), 'center');
      }, 500);
    }
  }, [messageCount, practiceReady, currentScreen, hasShownWelcome, hasShownPracticeReady]);

  // 根据用户消息内容给出智能回应
  useEffect(() => {
    if (!lastUserMessage || currentScreen !== 'chat') {return;}

    const message = lastUserMessage.toLowerCase();

    // 检测学习相关关键词
    if (message.includes('学习') || message.includes('练习') || message.includes('英语')) {
      setTimeout(() => {
        showTeacherMessage('我听到你提到了学习！这种积极的态度很棒！继续保持下去！📚', 'bottom-right');
      }, 2000);
    }

    // 检测困难或挫折情绪
    if (message.includes('难') || message.includes('不会') || message.includes('困难')) {
      setTimeout(() => {
        showTeacherMessage('不要担心！学习语言本来就需要时间和耐心。你已经在进步的路上了！💪', 'bottom-left');
      }, 1500);
    }

    // 检测成功或开心情绪
    if (message.includes('开心') || message.includes('高兴') || message.includes('成功')) {
      setTimeout(() => {
        showTeacherMessage('太好了！看到你这么开心我也很高兴！继续保持这种好心情学习吧！😊', 'bottom-right');
      }, 2000);
    }
  }, [lastUserMessage, currentScreen]);

  return (
    <TeacherAvatar
      showTeacher={guidance.showTeacher}
      message={guidance.message}
      position={guidance.position}
      onDismiss={hideTeacher}
    />
  );
};

export default TeacherGuidance;
